import { freeRecipient } from './recipientIds';
import type { EmailFormRecipient, ReplyToMessage } from './types';

export type ReplyRecipients = {
  to: EmailFormRecipient[];
  cc: EmailFormRecipient[];
  bcc: EmailFormRecipient[];
};

// Callers hand over `email_messages` jsonb (the feed passes the column as
// is): narrowed here, so no call site has to remember to.
const asChips = (list: unknown): EmailFormRecipient[] =>
  (Array.isArray(list) ? list : []).flatMap((entry: unknown) => {
    if (entry == null || typeof entry !== 'object') {
      return [];
    }
    const { email, name } = entry as { email?: unknown; name?: unknown };
    return typeof email === 'string' && email !== ''
      ? [freeRecipient(email, typeof name === 'string' ? name : undefined)]
      : [];
  });

const dedupe = (chips: EmailFormRecipient[]): EmailFormRecipient[] => {
  const seen = new Set<string>();
  return chips.filter(chip => {
    if (seen.has(chip.id)) {
      return false;
    }
    seen.add(chip.id);
    return true;
  });
};

// The agent's own addresses, for reply all to leave out: their CRM emails and
// the connected mailbox, which can be an alias or another provider than the
// login (`self_username` is the Nylas address, never in `emails`).
export const ownMailboxAddresses = (me: {
  emails?: ReadonlyArray<{ email: string }> | null;
  nylas_account?: ReadonlyArray<{ self_username?: string | null }> | null;
}): string[] => [
  ...(me.emails ?? []).map(entry => entry.email),
  ...(me.nylas_account ?? []).flatMap(login =>
    login.self_username != null ? [login.self_username] : [],
  ),
];

// The mail client rule, as the legacy composer applied it: a message WE sent
// is answered to its recipients; an inbound one to its reply-to when set,
// else to its sender. A plain reply drops cc and bcc (a copy the agent did
// not choose must not go out again, least of all a bcc). With `replyAll`,
// the original recipients join the To (minus the sender's own addresses)
// and cc / bcc come along. Everything is a bare-address chip: the caller may
// resolve CRM users and pass them instead.
export const replyRecipientsFor = (
  message: ReplyToMessage,
  ownEmails: string[] = [],
): ReplyRecipients => {
  const target =
    message.isSent === true
      ? message.to
      : message.replyTo != null && message.replyTo.length > 0
      ? message.replyTo
      : message.from;
  const own = new Set(ownEmails.map(email => email.trim().toLowerCase()));
  const notOwn = (chip: EmailFormRecipient) =>
    !own.has(chip.email.trim().toLowerCase());
  const to =
    message.replyAll === true && message.isSent !== true
      ? dedupe([...asChips(target), ...asChips(message.to)]).filter(notOwn)
      : asChips(target);
  const keepCopies = message.replyAll === true;
  return {
    to,
    cc: keepCopies ? asChips(message.cc).filter(notOwn) : [],
    bcc: keepCopies ? asChips(message.bcc).filter(notOwn) : [],
  };
};
